import { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson, fmtDate, fmtDateTime } from '../../../utils';
import OneDrivePicker, { type OneDriveFile } from '../../../components/OneDrivePicker';
import type { ChangeRequest, ChangeLog, ImplementationPlan, DeviationLogData, EvidenceLogData, Approver } from '../../../types';
import { useAuth } from '../../../context/AuthContext';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function ImplementationSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const { userName, userEmail } = useAuth();
  const plan = safeJson<Partial<ImplementationPlan>>(record.implementationPlan, {});

  const [deviations, setDeviations]       = useState<ChangeLog[]>([]);
  const [devLoading, setDevLoading]       = useState(true);
  const [showDevForm, setShowDevForm]     = useState(false);
  const [devDesc,     setDevDesc]         = useState('');
  const [devImpact,   setDevImpact]       = useState('');
  const [devSubmitting, setDevSubmitting] = useState(false);
  const [devErrors,   setDevErrors]       = useState<Record<string,string>>({});
  const [pendingFiles, setPendingFiles]   = useState<OneDriveFile[]>([]);
  const [evidenceDesc, setEvidenceDesc]   = useState('');
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [evidenceLogs, setEvidenceLogs]   = useState<ChangeLog[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(true);
  const [completeModal, setCompleteModal] = useState(false);
  const [completing, setCompleting]       = useState(false);

  async function loadDeviations() {
    try {
      const data = await api.get<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs?type=Deviation`);
      setDeviations(data);
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setDevLoading(false); }
  }

  async function loadEvidence() {
    try {
      const data = await api.get<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs?type=Evidence`);
      setEvidenceLogs(data);
    } catch { /* non-fatal */ }
    finally { setEvidenceLoading(false); }
  }

  useEffect(() => { loadDeviations(); loadEvidence(); }, [cid]);

  async function logDeviation() {
    const errs: Record<string,string> = {};
    if (!devDesc.trim())   errs.desc   = 'Description is required.';
    if (!devImpact.trim()) errs.impact = 'Impact assessment is required.';
    setDevErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setDevSubmitting(true);
    try {
      await api.post('/logs', {
        changeId: cid,
        changeName: record.changeName,
        logType: 'Deviation',
        loggedByName: userName,
        loggedByEmail: userEmail,
        loggedOn: new Date().toISOString(),
        logData: JSON.stringify({ description: devDesc.trim(), impactAssessment: devImpact.trim() } as DeviationLogData),
      });
      showToast('Deviation logged. Sponsor and affected stakeholders have been notified.', 'success');
      setDevDesc(''); setDevImpact(''); setShowDevForm(false);
      await loadDeviations();
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setDevSubmitting(false); }
  }

  async function attachEvidence() {
    if (pendingFiles.length === 0) { showToast('Please select at least one file from OneDrive.', 'warn'); return; }
    setUploadingEvidence(true);
    try {
      await Promise.all(pendingFiles.map(f =>
        api.post('/logs', {
          changeId: cid, changeName: record.changeName, logType: 'Evidence',
          loggedByName: userName, loggedByEmail: userEmail,
          loggedOn: new Date().toISOString(),
          logData: JSON.stringify({ fileName: f.name, fileDescription: evidenceDesc.trim(), fileUrl: f.url } satisfies EvidenceLogData),
        })
      ));
      setPendingFiles([]); setEvidenceDesc('');
      showToast(`${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''} attached.`, 'success');
      await loadEvidence();
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setUploadingEvidence(false); }
  }

  async function markComplete() {
    setCompleting(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/status`, { status: 'Under Review', currentStep: 'Review' });
      setCompleteModal(false);
      showToast('Implementation marked complete.', 'success');
      await onRefresh();
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setCompleting(false); }
  }

  return (
    <>
      {/* Summary */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-rocket" aria-hidden="true" /> Step 6 — Implementation</h2>
        </div>
        <div className="section-panel__body">
          <div className="field-display-grid">
            <div className="field-display"><div className="field-display__label">Change ID</div><div className="field-display__value change-id-mono">{record.changeId}</div></div>
            <div className="field-display"><div className="field-display__label">Approver</div><div className="field-display__value">{safeJson<Approver[]>(record.approvers, []).map(a => a.name).join(', ')}</div></div>
            <div className="field-display"><div className="field-display__label">Confirmed Go-Live</div><div className="field-display__value">{fmtDate(record.confirmedGolive)}</div></div>
            <div className="field-display"><div className="field-display__label">Stabilisation</div><div className="field-display__value">{plan.stabilisationPeriod ?? '—'}</div></div>
            <div className="field-display"><div className="field-display__label">Approach</div><div className="field-display__value">{plan.approach ?? '—'}</div></div>
            <div className="field-display"><div className="field-display__label">Plan Version</div><div className="field-display__value">{plan.planVersion ?? '—'}</div></div>
          </div>
          {plan.uploadedFileUrl && (
            <div style={{ marginTop: 12 }}>
              <a href={plan.uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary btn--sm">
                <i className="fa-solid fa-file-lines" aria-hidden="true" /> View Plan Document
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Deviation log */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Deviation Log</h2>
          <button className="btn btn--secondary btn--sm" onClick={() => setShowDevForm(p => !p)}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> Log Deviation
          </button>
        </div>
        <div className="section-panel__body">
          {showDevForm && (
            <div style={{ marginBottom: 24, padding: 20, background: 'var(--surface-muted)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <h3 style={{ marginBottom: 16 }}>New Deviation</h3>
              <div className="form-grid">
                <div className="form-group form-group--full">
                  <label className="form-label" htmlFor="dev-desc">Description <span className="required">*</span></label>
                  <textarea id="dev-desc" className={`form-control${devErrors.desc ? ' is-error' : ''}`} rows={3} value={devDesc} onChange={e => setDevDesc(e.target.value)} />
                  {devErrors.desc && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {devErrors.desc}</span>}
                </div>
                <div className="form-group form-group--full">
                  <label className="form-label" htmlFor="dev-impact">Impact Assessment <span className="required">*</span></label>
                  <textarea id="dev-impact" className={`form-control${devErrors.impact ? ' is-error' : ''}`} rows={3} value={devImpact} onChange={e => setDevImpact(e.target.value)} />
                  {devErrors.impact && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {devErrors.impact}</span>}
                </div>
              </div>
              <p className="form-hint" style={{ marginTop: 4 }}>The sponsor and all affected stakeholders will be notified automatically when this deviation is saved.</p>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button className={`btn btn--primary btn--sm${devSubmitting ? ' is-loading' : ''}`} onClick={logDeviation} disabled={devSubmitting}><i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save</button>
                <button className="btn btn--secondary btn--sm" onClick={() => { setShowDevForm(false); setDevDesc(''); setDevImpact(''); setDevErrors({}); }}>Cancel</button>
              </div>
            </div>
          )}
          <div className="table-wrapper">
            <table className="table" aria-label="Deviation log">
              <thead><tr><th>Logged On</th><th>Description</th><th>Impact</th><th>Notified</th></tr></thead>
              <tbody>
                {devLoading && <tr><td colSpan={4}><div className="loading-state" style={{ padding: 32 }}><div className="spinner" /></div></td></tr>}
                {!devLoading && deviations.length === 0 && <tr><td colSpan={4} className="table-empty">No deviations logged.</td></tr>}
                {deviations.map(d => {
                  const data = safeJson<DeviationLogData>(d.logData, { description: '', impactAssessment: '' });
                  return (
                    <tr key={d.id}>
                      <td data-label="Logged On">{fmtDateTime(d.loggedOn)}</td>
                      <td data-label="Description" style={{ fontSize: '0.8125rem' }}>{data.description}</td>
                      <td data-label="Impact" style={{ fontSize: '0.8125rem' }}>{data.impactAssessment}</td>
                      <td data-label="Notified"><span className="badge badge--accent">Notified</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Evidence upload */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-paperclip" aria-hidden="true" /> Evidence</h2>
        </div>
        <div className="section-panel__body">
          {/* Staging area */}
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-group form-group--full">
              <label className="form-label">Add Files from OneDrive</label>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <OneDrivePicker
                  onSelect={f => setPendingFiles(p => [...p, f])}
                  disabled={uploadingEvidence}
                  label="Choose from OneDrive"
                />
                {pendingFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {pendingFiles.map((f, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: 'var(--ink-dim)' }}>
                        <i className="fa-solid fa-file-lines" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                        {f.name}
                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-dim)', padding: 0, lineHeight: 1 }} onClick={() => setPendingFiles(p => p.filter((_, j) => j !== i))} aria-label="Remove">
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group form-group--full">
              <label className="form-label" htmlFor="evidence-desc">Description</label>
              <input type="text" id="evidence-desc" className="form-control" placeholder="Brief description…" value={evidenceDesc} onChange={e => setEvidenceDesc(e.target.value)} />
            </div>
          </div>
          <button className={`btn btn--secondary${uploadingEvidence ? ' is-loading' : ''}`} onClick={attachEvidence} disabled={uploadingEvidence || pendingFiles.length === 0}>
            <i className="fa-solid fa-link" aria-hidden="true" /> Attach{pendingFiles.length > 1 ? ` ${pendingFiles.length} Files` : ' Evidence'}
          </button>

          {/* Persisted evidence logs */}
          <div style={{ marginTop: 20 }}>
            {evidenceLoading && <div className="loading-state" style={{ padding: 16 }}><div className="spinner" /></div>}
            {!evidenceLoading && evidenceLogs.length === 0 && <p className="text-muted" style={{ fontSize: '0.875rem' }}>No evidence attached yet.</p>}
            {evidenceLogs.map(log => {
              const d = safeJson<EvidenceLogData>(log.logData, { fileName: '', fileDescription: '', fileUrl: '' });
              return (
                <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8, background: 'var(--surface-muted)' }}>
                  <i className="fa-solid fa-file-lines" style={{ color: 'var(--primary)', flexShrink: 0 }} aria-hidden="true" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>{d.fileName || 'File'}</a>
                    {d.fileDescription && <div style={{ fontSize: '0.8125rem', color: 'var(--ink-dim)' }}>{d.fileDescription}</div>}
                    <div style={{ fontSize: '0.8125rem', color: 'var(--ink-dim)' }}>{log.loggedByName} · {fmtDate(log.loggedOn)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mark complete */}
      {(record.status === 'Approved' || record.status === 'In Implementation') && (
        <div className="alert alert--success">
          <span className="alert__icon"><i className="fa-solid fa-flag-checkered" aria-hidden="true" /></span>
          <div className="alert__body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div className="alert__title">Implementation Complete</div>
              <p>Mark this change as complete to proceed to post-implementation review.</p>
            </div>
            <button className="btn btn--accent" onClick={() => setCompleteModal(true)}>
              <i className="fa-solid fa-circle-check" aria-hidden="true" /> Mark as Complete
            </button>
          </div>
        </div>
      )}

      <Modal open={completeModal} onClose={() => setCompleteModal(false)} title="Confirm Implementation Complete"
        footer={
          <>
            <button className="btn btn--secondary" onClick={() => setCompleteModal(false)}>Cancel</button>
            <button className={`btn btn--accent${completing ? ' is-loading' : ''}`} onClick={markComplete} disabled={completing}>
              <i className="fa-solid fa-circle-check" aria-hidden="true" /> Confirm
            </button>
          </>
        }
      >
        <p>Are you sure you want to mark this change as implementation complete? This will move the record into post-implementation review.</p>
      </Modal>
    </>
  );
}
